import superagent from 'superagent';

const API_ROOT = process.env.REACT_APP_API_ROOT;

const encode = encodeURIComponent;
const responseBody = res => res.body;

let token = null;
const tokenPlugin = req => {
  if (token) {
    req.set('authorization', `Bearer ${token}`);
  }
};

let username = null;
const usernamePlugin = req => {
  if (username) {
    req.set('X-username', username);
  }
};

const defaultDistance = 10000000;
const limit = (count, p) => `limit=${count}&offset=${p ? p * count : 0}`;
const omitSlug = article => Object.assign({}, article, { slug: undefined });

const requests = {
  del: url =>
    superagent.del(`${API_ROOT}${url}`).use(tokenPlugin).use(usernamePlugin).then(responseBody),
  get: url =>
    superagent.get(`${API_ROOT}${url}`).use(tokenPlugin).use(usernamePlugin).then(responseBody),
  put: (url, body) =>
    superagent.put(`${API_ROOT}${url}`, body).use(tokenPlugin).use(usernamePlugin).then(responseBody),
  post: (url, body) =>
    superagent.post(`${API_ROOT}${url}`, body).use(tokenPlugin).use(usernamePlugin).then(responseBody),
  patch: (url, body) =>
    superagent.patch(`${API_ROOT}${url}`, body).use(tokenPlugin).use(usernamePlugin).then(responseBody),
};

const Articles = {
  all: (page, device = 'desktop') =>
    requests.get(`/articles?${limit(10, page)}&device=${device}`),
  byAuthor: (author, page, device = 'desktop') =>
    requests.get(`/articles?author=${encode(author)}&${limit(5, page)}&device=${device}`),
  byTag: (tag, page, device = 'desktop') =>
    requests.get(`/articles?tag=${encode(tag)}&${limit(10, page)}&device=${device}`),
  del: slug =>
    requests.del(`/articles/${slug}`),
  favorite: slug =>
    requests.post(`/articles/${slug}/favorite`),
  favoritedBy: (author, page) =>
    requests.get(`/articles?favorited=${encode(author)}&${limit(5, page)}`),
  feed: (page, device = 'desktop') =>
    requests.get(`/articles/feed?${limit(5, page)}&device=${device}`),
  get: (slug, device = 'desktop') =>
    requests.get(`/articles/${slug}?device=${device}`),
  unfavorite: slug =>
    requests.del(`/articles/${slug}/favorite`),
  update: article =>
    requests.put(`/articles/${article.slug}`, { article: omitSlug(article) }),
  create: article =>
    requests.post('/articles', { article }),
};

const Auth = {
  current: () =>
    requests.get('/user'),
  login: (email, password) =>
    requests.post('/users/login', { user: { email, password } }),
  register: (username, email, password, token, context, birthDate) =>
    requests.post('/users', { user: { username, email, password, token, context, birthDate } }),
  resetPassword: (email, token) =>
    requests.post('/users/reset-password', { email, token }),
  setupPassword: (userId, token, password) =>
    requests.post(`/users/reset-password/${userId}/${token}`, { password }),
  confirmEmail: (userId, token) =>
    requests.post(`/users/confirm-email/${userId}/${token}`),
  save: user =>
    requests.put('/user', { user }),
  logout: () =>
    requests.get('/logout'),
};

const Comments = {
  createForArticle: (slug, comment) =>
    requests.post(`/articles/${slug}/comments`, { comment }),
  createForFreak: (slug, comment) =>
    requests.post(`/freaks/${slug}/comments`, { comment }),
  delete: (slug, commentId) =>
    requests.del(`/freaks/${slug}/comments/${commentId}`),
  forArticle: slug =>
    requests.get(`/articles/${slug}/comments`),
  forFreak: slug =>
    requests.get(`/freaks/${slug}/comments`),
  forCampaign: slug =>
    requests.get(`/campaigns/${slug}/comments`),
};

const FreaksPlaces = {
  all: (context, showPublic, showPrivate, southWestLat, southWestLon, northEastLat, northEastLon, searchQuery = '') =>
    requests.get(`/freaks-places?context=${context}&showPublic=${showPublic}&showPrivate=${showPrivate}&southWestLat=${southWestLat}&southWestLon=${southWestLon}&northEastLat=${northEastLat}&northEastLon=${northEastLon}${searchQuery ? `&searchQuery=${encodeURIComponent(searchQuery)}` : ''}`),
  restricted: (context, southWestLat, southWestLon, northEastLat, northEastLon, searchQuery = '') =>
    requests.get(`/freaks-places/restricted?context=${context}&southWestLat=${southWestLat}&southWestLon=${southWestLon}&northEastLat=${northEastLat}&northEastLon=${northEastLon}${searchQuery ? `&searchQuery=${encodeURIComponent(searchQuery)}` : ''}`),
  create: freakPlace =>
    requests.post('/freaks-places', { freakPlace }),
  update: freakPlace =>
    requests.patch(`/freaks-places/${freakPlace.slug}`, { freakPlace: omitSlug(freakPlace) }),
  del: freakPlaceId =>
    requests.del(`/freaks-places/${freakPlaceId}`),
  favorite: slug =>
    requests.post(`/freaks-places/${slug}/favorite`),
  unfavorite: slug =>
    requests.del(`/freaks-places/${slug}/favorite`),
  get: (slug, context, device = 'desktop') =>
    requests.get(`/freaks-places/${slug}?device=${device}&context=${context}`),
  getRestricted: (slug, context, device = 'desktop') =>
    requests.get(`/freaks-places/restricted/${slug}?device=${device}&context=${context}`),
  sendForPublic: slug =>
    requests.post(`/freaks-places/${slug}/forPublic`),
};

const FreakActivities = {
  create: freakActivity =>
    requests.post('/freaks-activities', { freakActivity }),
  update: freakActivity =>
    requests.put(`/freaks-activities/${freakActivity.slug}`, { freakActivity: omitSlug(freakActivity) }),
};

const Campaigns = {
  all: (
    page,
    context,
    device = 'desktop',
    latitude,
    longitude,
    distance = defaultDistance,
    sortByLikes = 'none',
    sortByDate = 'none',
    filterByName = 'none',
    filterByDateFrom = 'none',
    filterByDateTo = 'none',
    filterByStatus = 'none',
    filterByParticipant = 'none',
  ) =>
    requests.get(`/campaigns?${limit(5, page)}&device=${device}&context=${context}&latitude=${latitude}&longitude=${longitude}&distance=${distance}&sortByLikes=${sortByLikes}&sortByDate=${sortByDate}&filterByName=${filterByName}&filterByDateFrom=${filterByDateFrom}&filterByDateTo=${filterByDateTo}&filterByStatus=${filterByStatus}&filterByParticipant=${filterByParticipant}`),
  create: campaign =>
    requests.post('/campaigns', { campaign }),
  update: campaign =>
    requests.put(`/campaigns/${campaign.slug}`, { campaign: omitSlug(campaign) }),
  favorite: slug =>
    requests.post(`/campaigns/${slug}/favorite`),
  unfavorite: slug =>
    requests.del(`/campaigns/${slug}/favorite`),
  start: campaignId =>
    requests.post(`/campaigns/${campaignId}/start`),
  finish: campaignId =>
    requests.post(`/campaigns/${campaignId}/finish`),
  get: (slug, device = 'desktop') =>
    requests.get(`/campaigns/${slug}?device=${device}`),
  requestContact: () =>
    requests.post('/campaigns/requestContact'),
};

const Feeds = {
  daily: (
    page,
    context,
    device = 'desktop',
    latitude,
    longitude,
    distance = defaultDistance,
    sortByLikes = 'none',
    sortByDate = 'none',
    filterByName = 'none',
    filterByDateFrom = 'none',
    filterByDateTo = 'none',
    filterByStatus = 'none',
    filterByParticipant = 'none',
    feedForUsername = '',
  ) =>
    requests.get(`/feed/daily?${limit(3, page)}&device=${device}&context=${context}&latitude=${latitude}&longitude=${longitude}&distance=${distance}&sortByLikes=${sortByLikes}&sortByDate=${sortByDate}&filterByName=${filterByName}&filterByDateFrom=${filterByDateFrom}&filterByDateTo=${filterByDateTo}&filterByStatus=${filterByStatus}&filterByParticipant=${filterByParticipant}&feedForUsername=${feedForUsername}`),
  createUserFeedForYouTube: forHandle =>
    requests.post(`/feed/youtube/${forHandle}`),
  list: (social, username) =>
    requests.get(`/feed/social/${social}/${username}`),
  disconnectSocial: socialId =>
    requests.post(`/feed/youtube/${socialId}/disconnect`),
  getTopFeeds: page =>
    requests.get(`/feed/top-feeds/top`),
};

const Freaks = {
  requestTrackAccess: slug =>
    requests.post(`/freaks/${encodeURIComponent(slug)}/track-access-request`),
  availableContexts: () => requests.get('/freaks/availableContexts'),
  all: (
    page,
    context,
    device = 'desktop',
    latitude,
    longitude,
    distance = defaultDistance,
    sortByLikes = 'none',
    sortByDate = 'none',
    filterByName = 'none',
    filterByDateFrom = 'none',
    filterByDateTo = 'none',
    filterByStatus = 'none',
    filterByParticipant = 'none',
  ) =>
    requests.get(`/freaks?${limit(2, page)}&device=${device}&context=${context}&latitude=${latitude}&longitude=${longitude}&distance=${distance}&sortByLikes=${sortByLikes}&sortByDate=${sortByDate}&filterByName=${filterByName}&filterByDateFrom=${filterByDateFrom}&filterByDateTo=${filterByDateTo}&filterByStatus=${filterByStatus}&filterByParticipant=${filterByParticipant}`),
  create: freak =>
    requests.post('/freaks', { freak }),
  update: freak =>
    requests.put(`/freaks/${freak.slug}`, { freak: omitSlug(freak) }),
  del: freakId =>
    requests.del(`/freaks/${freakId}`),
  cancel: freakId =>
    requests.post(`/freaks/${freakId}/cancel`),
  get: (slug, device = 'desktop') =>
    requests.get(`/freaks/${slug}?device=${device}`),
  feed: (
    page,
    context,
    device = 'desktop',
    latitude,
    longitude,
    distance = defaultDistance,
    sortByLikes = 'none',
    sortByDate = 'none',
    filterByName = 'none',
    filterByDateFrom = 'none',
    filterByDateTo = 'none',
    filterByStatus = 'none',
    filterByParticipant = 'none',
    feedForUsername = '',
  ) =>
    requests.get(`/freaks/feed?${limit(1, page)}&device=${device}&context=${context}&latitude=${latitude}&longitude=${longitude}&distance=${distance}&sortByLikes=${sortByLikes}&sortByDate=${sortByDate}&filterByName=${filterByName}&filterByDateFrom=${filterByDateFrom}&filterByDateTo=${filterByDateTo}&filterByStatus=${filterByStatus}&filterByParticipant=${filterByParticipant}&feedForUsername=${feedForUsername}`),
  followed: (
    page,
    context,
    device = 'desktop',
    latitude,
    longitude,
    distance = defaultDistance,
    sortByLikes = 'none',
    sortByDate = 'none',
    filterByName = 'none',
    filterByDateFrom = 'none',
    filterByDateTo = 'none',
    filterByStatus = 'none',
    filterByParticipant = 'none',
    feedForUsername = '',
  ) =>
    requests.get(`/freaks/followed?${limit(2, page)}&device=${device}&context=${context}&latitude=${latitude}&longitude=${longitude}&distance=${distance}&sortByLikes=${sortByLikes}&sortByDate=${sortByDate}&filterByName=${filterByName}&filterByDateFrom=${filterByDateFrom}&filterByDateTo=${filterByDateTo}&filterByStatus=${filterByStatus}&filterByParticipant=${filterByParticipant}&feedForUsername=${feedForUsername}`),
  forFreakHub: (
    page,
    context = 'wof',
    device = 'desktop',
    feedForFreakHub = '',
  ) =>
    requests.get(`/freaks/forFreakHub?${limit(2, page)}&device=${device}&context=${context}&feedForFreakHub=${feedForFreakHub}`),
  favorite: slug =>
    requests.post(`/freaks/${slug}/favorite`),
  favoritedBy: (author, page, context, device = 'desktop' ) =>
    requests.get(`/freaks?favorited=${encode(author)}&${limit(5, page)}&device=${device}&context=${context}`),
  unfavorite: slug =>
    requests.del(`/freaks/${slug}/favorite`),
  join: freakId =>
    requests.post(`/freaks/${freakId}/join`),
  start: freakId =>
    requests.post(`/freaks/${freakId}/start`),
  finish: freakId =>
    requests.post(`/freaks/${freakId}/finish`),
  publish: freakId =>
    requests.post(`/freaks/${freakId}/publish`),
  deleteParticipant: (freakId, participantId) =>
    requests.post(`/freaks/${freakId}/${participantId}/delete`),
  nearby: (latitude, longitude, distance = 100, context, device = 'desktop', sortBy= 'createdAt', sortOrder = 'desc') =>
    requests.get(`/freaks/nearby?latitude=${latitude}&longitude=${longitude}&distance=${distance}&device=${device}&sortBy=${sortBy}&sortOrder=${sortOrder}&context=${context}`),
  deleteYouTubeLink: (freakId, youTubeLinkId) =>
    requests.post(`/freaks/${freakId}/youtube/${youTubeLinkId}/delete`),
  deleteFacebookLink: (freakId, facebookLinkId) =>
    requests.post(`/freaks/${freakId}/facebook/${facebookLinkId}/delete`),
  deleteInstagramLink: (freakId, instagramLinkId) =>
    requests.post(`/freaks/${freakId}/instagram/${instagramLinkId}/delete`),
  deleteXLink: (freakId, xLinkId) =>
    requests.post(`/freaks/${freakId}/x/${xLinkId}/delete`),
  reportAbuse: (slug, abuseReason) =>
    requests.post(`/freaks/${slug}/report-abuse/${abuseReason}`),
  restricted: (page, context, device = 'desktop') =>
    requests.get(`/freaks/restricted?${limit(5, page)}&device=${device}&context=${context}`),
  getTopFreaks: page =>
    requests.get(`/freaks/top-freaks/top?${limit(10, page)}`),
  getMyPositionInTopFreaks: () =>
    requests.get('/freaks/top-freaks/my-position'),
  leave: (freakId, participantId) =>
    requests.post(`/freaks/${freakId}/leave/${participantId}`),
  updateRoute: (slug, freakRoute, finished = false) =>
    requests.post(`/freaks/${slug}/updateRoute`, { freakRoute, ...(finished ? { finished: true } : {}) }),
  getTracks: slug =>
    requests.get(`/freaks/${encodeURIComponent(slug)}/tracks`),
  createRoute: (slug, freakRoute) =>
      requests.post(`/freaks/${slug}/createRoute`, { freakRoute }),
};

const Notifications = {
  feed: page =>
    requests.get(`/notifications/feed?${limit(10, page)}`),
  count: () =>
    requests.get('/notifications/count'),
  read: notificationId =>
    requests.post(`/notifications/${notificationId}/read`),
  approve: notificationUrl =>
    requests.post(notificationUrl),
  decline: notificationUrl =>
    requests.post(notificationUrl),
  delete: (notificationId) =>
    requests.del(`/notifications/${notificationId}/delete`),
};

const NotificationPreferences = {
  list: () =>
    requests.get('/users/notification-preferences'),
  update: (context, type, enabled) =>
    requests.patch('/users/notification-preferences', { context, type, enabled }),
};

const Profile = {
  follow: username =>
    requests.post(`/profiles/${username}/follow`),
  get: username =>
    requests.get(`/profiles/${username}`),
  getAll: () =>
    requests.get('/profiles'),
  getAllForParticipants: () =>
    requests.get('/profiles/participants'),
  unfollow: username =>
    requests.del(`/profiles/${username}/follow`),
  getProfileFollowers: username =>
    requests.get(`/profiles/followers/${username}`),
  getProfileFollow: username =>
    requests.get(`/profiles/following/${username}`),
  count: () =>
    requests.get('/profiles/count'),
  block: username =>
    requests.post(`/profiles/${username}/block`),
  unblock: username =>
    requests.del(`/profiles/${username}/block`),
  blocked: () =>
    requests.get('/user/blocked'),
};

const Abuse = {
  // abuse: { reason, description, reportedUserId?, freakId?, commentId? }
  report: abuse =>
    requests.post('/abuse/report', { abuse }),
};

const Tags = {
  getAll: () => requests.get('/tags')
};

const Upload = {
  getPreSignedUrl: (path, fileSizeBytes) => requests.get(`/files/upload/pre-signed/${path}/${fileSizeBytes}`),
  createImagesForDevices: fileMetaData => requests.post('/files/upload/create-for-devices', { fileMetaData }),
};

const Images = {
  del: imageId => requests.del(`/images/${imageId}`),
};

const Chat = {
  getConfig: () => requests.get('/chat/config'),
  getToken: () => requests.get('/chat/authToken'),
  notify: (recipientUsernames, messageContent) =>
    requests.post('/chat/notify', { recipientUsernames, messageContent }),
};

const Firebase = {
  setFcmToken: fcmToken => requests.post(`/firebase/fcm/${fcmToken}`),
};

const Points = {
  count: () => requests.get('/points'),
};

const Shake = {
  check: () => requests.get('/shake-it'),
  shake: () => requests.post('/shake-it'),
};

const Price = {
  getHistory: page => requests.get(`/prices/history?${limit(10, page)}`),
};

const FreakHubs = {
  get: (slug, device = 'desktop') =>
    requests.get(`/freak-hubs/${slug}?device=${device}`),
  getUsers: (page, slug) =>
    requests.get(`/freak-hubs/${slug}/users?${limit(3, page)}`),
  explore: (
    page,
    context = 'wof',
    device = 'desktop',
  ) =>
    requests.get(`/freak-hubs/explore?${limit(3, page)}&device=${device}&context=${context}`),
  feed: (
    page,
    context = 'wof',
    device = 'desktop',
  ) =>
    requests.get(`/freak-hubs/feed?${limit(3, page)}&device=${device}&context=${context}`),
  getAll: (
    context = 'wof',
    device = 'desktop',
  ) => requests.get(`/freak-hubs/all?device=${device}&context=${context}`),
  deleteParticipant: (freakHubId, participantId) =>
    requests.post(`/freak-hubs/${freakHubId}/${participantId}/delete`),
  create: freakHub =>
    requests.post('/freak-hubs', { freakHub }),
  update: freakHub =>
    requests.patch(`/freak-hubs/${freakHub.slug}`, { freakHub: omitSlug(freakHub) }),
  join: freakHubId =>
    requests.post(`/freak-hubs/${freakHubId}/join`),
  del: freakHubId =>
    requests.del(`/freak-hubs/${freakHubId}`),
};

const FreakItems = {
  categories: context =>
    requests.get(`/freak-item-categories?context=${context}`),
  all: (context, username) =>
    requests.get(`/freak-items/all?context=${context}${username ? `&username=${encode(username)}` : ''}`),
  get: slug =>
    requests.get(`/freak-items/${slug}`),
  create: freakItem =>
    requests.post('/freak-items', { freakItem }),
  update: (slug, freakItem) =>
    requests.put(`/freak-items/${slug}`, { freakItem }),
  del: slug =>
    requests.del(`/freak-items/${slug}`),
};

const Subscribe = {
  createForFreakHub: (freakHubId) => requests.post(`/subscribe/freak-hub/${freakHubId}`),
  deleteForFreakHub: (freakHubId) => requests.del(`/subscribe/freak-hub/${freakHubId}`),
};

const AmbassadorGifts = {
  get: () => requests.get('/ambassador/gifts'),
};

const Reels = {
  all: (page = 0) => requests.get(`/reels?page=${page}&pageSize=10`),
  byFreak: freakId => requests.get(`/reels/freak/${freakId}`),
  byFreakPlace: id => requests.get(`/reels/freak-place/${id}`),
  create: data => requests.post('/reels', data),
  like: slug => requests.post(`/reels/${slug}/like`),
  unlike: slug => requests.del(`/reels/${slug}/like`),
  delete: slug => requests.del(`/reels/${slug}`),
};

const Music = {
  config: () => requests.get('/music/config'),
  search: (q, limit = 20, offset = 0) =>
    requests.get(`/music/search?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}`),
};

const Moderation = {
  getPending: () => requests.get('/admin/moderation'),
  approve: (kind, slug) => requests.put(`/admin/moderation/${kind}/${encodeURIComponent(slug)}/approve`),
  reject: (kind, slug, reason) =>
    requests.put(`/admin/moderation/${kind}/${encodeURIComponent(slug)}/reject`, { reason }),
};

const Feedback = {
  create: feedback => requests.post('/feedback', { feedback }),
  getAll: () => requests.get('/feedback'),
  sendContact: contact => requests.post('/feedback/contact', contact),
};

const FishingPrediction = {
  get: freakPlaceId => requests.get(`/fishing-prediction/${freakPlaceId}`),
};

const Weather = {
  get: freakPlaceId => requests.get(`/weather/${freakPlaceId}`),
};

const FishSpecies = {
  getAll: () => requests.get('/fish-species'),
};

const CatchLog = {
  create: catchLog => requests.post('/catch-logs', catchLog),
  getAll: () => requests.get('/catch-logs'),
  getForPlace: freakPlaceId => requests.get(`/catch-logs/place/${freakPlaceId}`),
  update: (id, data) => requests.put(`/catch-logs/${id}`, data),
  delete: id => requests.del(`/catch-logs/${id}`),
  uploadImage: file =>
    superagent
      .post(`${API_ROOT}/catch-logs/upload-image`)
      .use(tokenPlugin)
      .use(usernamePlugin)
      .attach('file', file, file.name)
      .then(responseBody),
};

// The Freaks — /the-freaks (Crims-style daily game, same API as the mobile app). Game actions answer {result, state}.
// X-Freaks-Client: game version of this client (2 = can repair the base; the API only starts base wear for 2+).
export const FREAKS_CLIENT = '2';
let freaksLanguage = null;
const freaksPlugin = req => {
  req.set('X-Freaks-Client', FREAKS_CLIENT);
  // mission / gear names come back in the app language
  if (freaksLanguage) req.query({ lang: freaksLanguage });
};
const freaksRequests = {
  get: (url, query = {}) =>
    superagent.get(`${API_ROOT}/the-freaks${url}`).query(query)
      .use(tokenPlugin).use(usernamePlugin).use(freaksPlugin).then(responseBody),
  post: (url, body = {}) =>
    superagent.post(`${API_ROOT}/the-freaks${url}`).send(body)
      .use(tokenPlugin).use(usernamePlugin).use(freaksPlugin).then(responseBody),
};

const TheFreaks = {
  setLanguage: language => { freaksLanguage = language; },
  get: () => freaksRequests.get('').then(data => data.state),
  status: () => freaksRequests.get('/status'),
  profile: username => freaksRequests.get(`/players/${encode(username)}`),
  // times > 1: "Repeat" plays the mission several times in a row
  playMission: (key, times) => freaksRequests.post(`/missions/${encode(key)}`, times && times > 1 ? { times } : {}),
  train: stat => freaksRequests.post(`/train/${stat}`),
  buyGear: key => freaksRequests.post(`/gear/${encode(key)}`),
  upgradeBase: () => freaksRequests.post('/base/upgrade'),
  collectBase: () => freaksRequests.post('/base/collect'),
  repairBase: () => freaksRequests.post('/base/repair'),
  refill: () => freaksRequests.post('/refill'),
  recover: () => freaksRequests.post('/recover'),
  claimDaily: () => freaksRequests.post('/daily'),
  claimQuest: key => freaksRequests.post(`/quests/${encode(key)}/claim`),
  deposit: amount => freaksRequests.post('/safe/deposit', { amount }),
  withdraw: amount => freaksRequests.post('/safe/withdraw', { amount }),
  changeContext: context => freaksRequests.post('/context', { context }),
  challenge: (username, isBot = false) =>
    freaksRequests.post(`/challenges/${encode(username)}${isBot ? '?bot=1' : ''}`),
  targets: () => freaksRequests.get('/targets')
    .then(data => ({ targets: data.targets ?? [], newcomerUntil: data.newcomerUntil ?? null })),
  top: (board, context) => freaksRequests.get('/top', { board, ...(context ? { context } : {}) }),
  // awards=1: this app can show medal / badge events
  today: () => freaksRequests.get('/today', { awards: 1 }),
  friends: () => freaksRequests.get('/friends').then(data => data.friends),
  invite: username => freaksRequests.post(`/invites/${encode(username)}`),
};

export default {
  Articles,
  Auth,
  Comments,
  Campaigns,
  Freaks,
  Feeds,
  FreaksPlaces,
  FreakActivities,
  Notifications,
  NotificationPreferences,
  Profile,
  Abuse,
  Tags,
  Upload,
  Images,
  Chat,
  Firebase,
  Points,
  Shake,
  Price,
  FreakHubs,
  FreakItems,
  Subscribe,
  AmbassadorGifts,
  Reels,
  Music,
  Feedback,
  Moderation,
  FishingPrediction,
  FishSpecies,
  CatchLog,
  Weather,
  TheFreaks,
  setToken: _token => { token = _token; },
  getToken: () => token,
  setUserName: _username => { username = _username },
};
